/**
 * ReorderSection — Bestellliste (Nachbestellung) gruppiert nach Lieferant.
 *
 * Zwei Abschnitte:
 *  1. „Offen" — noch nicht bestellte Positionen (aus dem Werkstatt-„Auf Bestellliste"-Button).
 *     Pro Lieferant: Sammel-Bestellung als E-Mail (versenden), CSV oder XML (Download).
 *     Nach Export/Versand → Status „bestellt".
 *  2. „Bestellt / unterwegs" — bestellte Positionen. Pro Lieferant: Wareneingang
 *     verbuchen (Sammel + einzelne Mengenanpassung + optionaler Lagerplatz).
 *     Lagerzugang nur für Positionen mit verknüpftem Artikel; Teilmengen möglich.
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ShoppingCart, Mail, FileSpreadsheet, FileCode, Trash2, Inbox, PackageCheck, ScanLine, Flame } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";

function groupBySupplier(items, supById) {
    const groups = {};
    for (const it of items) {
        const key = it.supplier_id || "__none__";
        (groups[key] = groups[key] || []).push(it);
    }
    return Object.keys(groups)
        .sort((a, b) => {
            const an = a === "__none__" ? "zzz" : (supById[a]?.name || "");
            const bn = b === "__none__" ? "zzz" : (supById[b]?.name || "");
            return an.localeCompare(bn);
        })
        .map((key) => ({ key, supplier: key === "__none__" ? null : supById[key], list: groups[key] }));
}

export default function ReorderSection({ items, suppliers, storageLocations = [], onReload, onEditArticle }) {
    const [busy, setBusy] = useState(null);
    const [recvQty, setRecvQty] = useState({});   // {itemId: qty}
    const [recvLoc, setRecvLoc] = useState({});   // {itemId: locationId}
    // Aug-2026 (User-Wunsch): „Dreh-Hüter" — meistverkaufte Artikel mit zu
    // niedrigem Mindestbestand (>5 Verkäufe/Monat, 90-Tage-Fenster).
    const [fastMovers, setFastMovers] = useState([]);
    const [fmBusy, setFmBusy] = useState(null);
    useEffect(() => {
        api.get("/articles/fast-movers").then((r) => setFastMovers(r.data?.items || [])).catch(() => {});
    }, []);
    const applyMinStock = async (fm) => {
        setFmBusy(fm.article.id);
        try {
            await api.patch(`/articles/${fm.article.id}`, { min_stock: fm.suggested_min_stock });
            setFastMovers((prev) => prev.filter((x) => x.article.id !== fm.article.id));
            toast.success(`${fm.article.sku}: Mindestbestand auf ${fm.suggested_min_stock} gesetzt`);
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        setFmBusy(null);
    };

    const supById = Object.fromEntries((suppliers || []).map((s) => [s.id, s]));
    const open = (items || []).filter((i) => (i.status || "open") === "open");
    const ordered = (items || []).filter((i) => i.status === "ordered");

    const openGroups = groupBySupplier(open, supById);
    const orderedGroups = groupBySupplier(ordered, supById);

    const doOrder = async (key, format) => {
        const supplier_id = key === "__none__" ? null : key;
        setBusy(`${key}:${format}`);
        try {
            const { data } = await api.post("/wms/reorder-list/order", { supplier_id, format });
            if (format === "email") {
                toast.success(`Bestellung an ${data.to} ${data.mocked ? "vorbereitet (Versand simuliert)" : "versendet"} · ${data.count} Position(en)`);
            } else {
                const blob = new Blob([data.content], { type: data.mime || "text/plain" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = data.filename || `bestellung.${format}`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success(`${format.toUpperCase()} erstellt · ${data.count} Position(en) auf „bestellt" gesetzt`);
            }
            onReload?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sammel-Bestellung fehlgeschlagen");
        }
        setBusy(null);
    };

    const removeItem = async (id) => {
        try {
            await api.delete(`/wms/reorder-list/${id}`);
            toast.success("Position entfernt");
            onReload?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    const openReceipt = async (receiptId) => {
        try {
            const res = await api.get(`/wms/goods-receipts/${receiptId}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) { toast.error("Beleg konnte nicht geöffnet werden"); }
    };

    // Wareneingang einer einzelnen Position
    const receiveItems = async (key, list) => {
        const payload = list
            .map((it) => {
                const qty = recvQty[it.id] != null ? Number(recvQty[it.id]) : Number(it.quantity);
                return { item_id: it.id, received_qty: qty, location_id: recvLoc[it.id] || null };
            })
            .filter((x) => x.received_qty > 0);
        if (payload.length === 0) { toast.error("Keine Menge zum Verbuchen"); return; }
        setBusy(`recv:${key}`);
        try {
            const { data } = await api.post("/wms/reorder-list/receive", { items: payload });
            if (data.receipt_id) {
                toast.success(`Wareneingang verbucht · ${data.received} Position(en), ${data.stock_booked} Lagerzugang/Lagerzugänge`, {
                    action: { label: "Beleg (PDF)", onClick: () => openReceipt(data.receipt_id) },
                    duration: 8000,
                });
            } else {
                toast.success(`Wareneingang verbucht · ${data.received} Position(en)`);
            }
            setRecvQty({}); setRecvLoc({});
            onReload?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Wareneingang fehlgeschlagen");
        }
        setBusy(null);
    };

    const empty = open.length === 0 && ordered.length === 0 && fastMovers.length === 0;
    if (empty) {
        return (
            <div className="text-center py-16 text-gray-500" data-testid="reorder-empty">
                <Inbox className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="font-bold text-gray-700">Keine Bestellpositionen</p>
                <p className="text-sm mt-1">Nicht vorrätige Teile werden im Werkstatt-Auftrag über „Auf Bestellliste" hier gesammelt.</p>
            </div>
        );
    }

    return (
        <div className="space-y-10" data-testid="reorder-section">
            {/* ── Aug-2026 (User-Wunsch): Dreh-Hüter — Schnelldreher mit zu niedrigem Mindestbestand ── */}
            {fastMovers.length > 0 && (
                <section className="border-2 border-rose-300 bg-rose-50 rounded-sm p-4" data-testid="fast-movers-block">
                    <h2 className="font-display text-base font-black text-rose-900 mb-1 flex items-center gap-2">
                        <Flame className="w-5 h-5" /> Dreh-Hüter: Schnelldreher mit zu niedrigem Mindestbestand ({fastMovers.length})
                    </h2>
                    <p className="text-[11px] text-rose-800 mb-2">Mehr als 5 Verkäufe/Monat (Ø letzte 90 Tage) — Mindestbestand anpassen, damit die Bestellliste rechtzeitig auslöst. Zeile anklicken = Artikel öffnen.</p>
                    <div className="divide-y divide-rose-200">
                        {fastMovers.map((fm) => (
                            <div key={fm.article.id}
                                onClick={() => onEditArticle?.(fm.article)}
                                className="py-1.5 flex items-center gap-3 text-xs cursor-pointer hover:bg-rose-100/60 px-1 rounded-sm"
                                data-testid={`fast-mover-${fm.article.id}`}>
                                <span className="font-mono-num font-bold shrink-0">{fm.article.sku}</span>
                                <span className="truncate flex-1">{fm.article.name}</span>
                                {fm.site_code && <span className="text-[10px] font-bold text-gray-500 shrink-0">{fm.site_code}</span>}
                                <span className="font-mono-num shrink-0" title="Ø Verkäufe pro Monat">Ø {fm.sold_per_month}/Monat</span>
                                <span className="font-mono-num shrink-0 text-gray-600" title="Aktueller Bestand">Bestand {Number(fm.article.stock_qty || 0)}</span>
                                <span className="shrink-0">Min: <b className="font-mono-num">{Number(fm.article.min_stock || 0)}</b> → <b className="font-mono-num text-rose-900">{fm.suggested_min_stock}</b></span>
                                <button type="button"
                                    onClick={(e) => { e.stopPropagation(); applyMinStock(fm); }}
                                    disabled={fmBusy === fm.article.id}
                                    className="shrink-0 text-[10px] font-bold uppercase bg-rose-700 text-white rounded-sm px-2 py-1 hover:bg-rose-800 disabled:opacity-50"
                                    data-testid={`fast-mover-apply-${fm.article.id}`}>
                                    {fmBusy === fm.article.id ? "…" : "Übernehmen"}
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            )}
            {/* ── Aug-2026 (User-Wunsch Jörg): Rückstands-Radar ─────────── */}
            {(() => {
                const backorders = (items || [])
                    .filter((i) => i.status !== "received" && /rückstand/i.test(i.note || ""))
                    .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
                if (backorders.length === 0) return null;
                const ageDays = (d) => Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000));
                return (
                    <section className="border-2 border-orange-300 bg-orange-50 rounded-sm p-4" data-testid="backorder-radar">
                        <h2 className="font-display text-base font-black text-orange-900 mb-1 flex items-center gap-2">
                            <PackageCheck className="w-5 h-5" /> Rückstands-Radar
                            <span className="bg-orange-200 text-orange-900 px-2 py-0.5 rounded-sm text-xs font-mono-num">{backorders.length}</span>
                        </h2>
                        <p className="text-[11px] text-orange-800 mb-3">Alle offenen Rückstandsteile aus Lieferschein-Scans — älteste zuerst. Beim nächsten Wareneingang werden sie automatisch verbucht.</p>
                        <ul className="space-y-1.5">
                            {backorders.map((b) => {
                                const age = ageDays(b.created_at);
                                return (
                                    <li key={b.id} className="bg-white border border-orange-200 rounded-sm px-3 py-2 flex flex-wrap items-center gap-2 text-xs" data-testid={`backorder-item-${b.id}`}>
                                        <span className="font-bold text-gray-900 min-w-0 truncate">{b.article_name}</span>
                                        {b.sku && <span className="font-mono-num text-gray-500">{b.sku}</span>}
                                        <span className="text-gray-600">{Number(b.quantity) || 1} Stk</span>
                                        <span className="text-gray-500">· {supById[b.supplier_id]?.name || (b.note || "").replace(/^Rückstand aus Lieferschein\s*/i, "Beleg ") || "Lieferant unbekannt"}</span>
                                        <span className={`ml-auto shrink-0 font-bold px-2 py-0.5 rounded-sm ${age >= 14 ? "bg-red-100 text-red-800" : age >= 7 ? "bg-orange-200 text-orange-900" : "bg-gray-100 text-gray-700"}`}
                                            title={`Auf der Rückstandsliste seit ${new Date(b.created_at).toLocaleDateString("de-DE")}`}>
                                            {age === 0 ? "heute" : `${age} Tag${age !== 1 ? "e" : ""}`}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-wider text-gray-400 shrink-0">{b.status === "ordered" ? "bestellt" : "offen"}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                );
            })()}

            {/* ── Abschnitt 1: Offen ──────────────────────────────────── */}
            <section data-testid="reorder-open-section">
                <h2 className="font-display text-lg font-black text-[#111827] mb-3 flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" /> Offen · zu bestellen
                    <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-sm text-xs">{open.length}</span>
                </h2>
                {open.length === 0 ? (
                    <p className="text-sm text-gray-500">Keine offenen Positionen.</p>
                ) : (
                    <div className="space-y-6">
                        {openGroups.map(({ key, supplier, list }) => {
                            const supName = supplier?.name || "Ohne Lieferant";
                            const totalQty = list.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
                            const hasEmail = !!supplier?.email;
                            return (
                                <div key={key} className="border border-gray-200 rounded-sm bg-white" data-testid={`reorder-group-${key}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
                                        <div>
                                            <div className="font-bold text-[#111827]">{supName}</div>
                                            <div className="text-xs text-gray-500">
                                                {list.length} Position(en) · {totalQty} Stk
                                                {supplier?.email ? ` · ${supplier.email}` : " · keine E-Mail hinterlegt"}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <button type="button" disabled={!hasEmail || busy === `${key}:email`} onClick={() => doOrder(key, "email")}
                                                title={hasEmail ? "Bestell-E-Mail an Lieferant senden" : "Keine E-Mail-Adresse beim Lieferanten hinterlegt"}
                                                className="text-xs uppercase tracking-wider font-bold inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border bg-[#111827] text-white border-[#111827] hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed"
                                                data-testid={`reorder-email-${key}`}>
                                                <Mail className="w-3.5 h-3.5" /> E-Mail senden
                                            </button>
                                            <button type="button" disabled={busy === `${key}:csv`} onClick={() => doOrder(key, "csv")}
                                                className="text-xs uppercase tracking-wider font-bold inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-40"
                                                data-testid={`reorder-csv-${key}`}>
                                                <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
                                            </button>
                                            <button type="button" disabled={busy === `${key}:xml`} onClick={() => doOrder(key, "xml")}
                                                className="text-xs uppercase tracking-wider font-bold inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-40"
                                                data-testid={`reorder-xml-${key}`}>
                                                <FileCode className="w-3.5 h-3.5" /> XML
                                            </button>
                                        </div>
                                    </div>
                                    <ItemTable list={list} onRemove={removeItem} />
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* ── Abschnitt 2: Bestellt / unterwegs → Wareneingang ────── */}
            <section data-testid="reorder-ordered-section">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <h2 className="font-display text-lg font-black text-[#111827] flex items-center gap-2">
                        <PackageCheck className="w-5 h-5" /> Bestellt / unterwegs
                        <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-sm text-xs">{ordered.length}</span>
                    </h2>
                    <Link to="/dealer/wms/scan-delivery" className="text-xs uppercase tracking-wider font-bold inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[#FFCC00] bg-[#FFCC00] text-[#111827] hover:bg-[#E6B800]" data-testid="reorder-scan-delivery-link">
                        <ScanLine className="w-3.5 h-3.5" /> Lieferschein scannen
                    </Link>
                </div>
                {ordered.length === 0 ? (
                    <p className="text-sm text-gray-500">Keine bestellten Positionen. Tipp: Per Lieferschein-Scan werden gelieferte bestellte Teile automatisch erkannt und verbucht.</p>
                ) : (
                    <div className="space-y-6">
                        {orderedGroups.map(({ key, supplier, list }) => {
                            const supName = supplier?.name || "Ohne Lieferant";
                            return (
                                <div key={key} className="border border-gray-200 rounded-sm bg-white" data-testid={`reorder-ordered-group-${key}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-sky-50 border-b border-sky-200">
                                        <div>
                                            <div className="font-bold text-[#111827]">{supName}</div>
                                            <div className="text-xs text-gray-500">{list.length} bestellte Position(en)</div>
                                        </div>
                                        <button type="button" disabled={busy === `recv:${key}`} onClick={() => receiveItems(key, list)}
                                            className="text-xs uppercase tracking-wider font-bold inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                                            data-testid={`reorder-receive-${key}`}>
                                            <PackageCheck className="w-3.5 h-3.5" /> Wareneingang verbuchen
                                        </button>
                                    </div>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
                                                <th className="px-4 py-2">Artikel</th>
                                                <th className="px-4 py-2">Art.-Nr. / EAN</th>
                                                <th className="px-4 py-2 text-right">Bestellt</th>
                                                <th className="px-4 py-2 text-right">Erhalten</th>
                                                <th className="px-4 py-2">Lagerplatz</th>
                                                <th className="px-4 py-2">Lager?</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {list.map((it) => (
                                                <tr key={it.id} className="border-b border-gray-50 last:border-0" data-testid={`reorder-ordered-item-${it.id}`}>
                                                    <td className="px-4 py-2 font-medium text-gray-900">{it.article_name}</td>
                                                    <td className="px-4 py-2 text-gray-500 text-xs">{it.sku || it.ean || "–"}</td>
                                                    <td className="px-4 py-2 text-right">{it.quantity}</td>
                                                    <td className="px-4 py-2 text-right">
                                                        <input type="number" min="0" step="1"
                                                            value={recvQty[it.id] != null ? recvQty[it.id] : it.quantity}
                                                            onChange={(e) => setRecvQty({ ...recvQty, [it.id]: e.target.value })}
                                                            className="w-20 border border-gray-300 rounded-sm px-2 py-1 text-right text-sm"
                                                            data-testid={`reorder-recv-qty-${it.id}`} />
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <select value={recvLoc[it.id] || ""} onChange={(e) => setRecvLoc({ ...recvLoc, [it.id]: e.target.value })}
                                                            className="border border-gray-300 rounded-sm px-2 py-1 text-xs max-w-[180px]"
                                                            data-testid={`reorder-recv-loc-${it.id}`}>
                                                            <option value="">Ohne Lagerplatz</option>
                                                            {storageLocations.map((l) => (
                                                                <option key={l.id} value={l.id}>{l.code}{l.name ? ` · ${l.name}` : ""}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="px-4 py-2 text-xs">
                                                        {it.article_id
                                                            ? <span className="text-emerald-700 font-bold">Bestand +</span>
                                                            : <span className="text-gray-400" title="Keine Artikel-Verknüpfung — wird nur als erhalten markiert">nur erhalten</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}

function ItemTable({ list, onRemove }) {
    return (
        <table className="w-full text-sm">
            <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-2">Artikel</th>
                    <th className="px-4 py-2">Art.-Nr. / EAN</th>
                    <th className="px-4 py-2 text-right">Menge</th>
                    <th className="px-4 py-2">Auftrag</th>
                    <th className="px-4 py-2">Notiz</th>
                    <th className="px-4 py-2"></th>
                </tr>
            </thead>
            <tbody>
                {list.map((it) => (
                    <tr key={it.id} className="border-b border-gray-50 last:border-0" data-testid={`reorder-item-${it.id}`}>
                        <td className="px-4 py-2 font-medium text-gray-900">{it.article_name}</td>
                        <td className="px-4 py-2 text-gray-500 text-xs">{it.sku || it.ean || "–"}</td>
                        <td className="px-4 py-2 text-right">{it.quantity}</td>
                        <td className="px-4 py-2 text-gray-500 text-xs">{it.work_order_number || "–"}</td>
                        <td className="px-4 py-2 text-gray-500 text-xs">{it.note || "–"}</td>
                        <td className="px-4 py-2 text-right">
                            <button type="button" onClick={() => onRemove(it.id)} className="text-gray-400 hover:text-red-600" title="Position entfernen" data-testid={`reorder-remove-${it.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
